/**
 * AdminNetworkHealth — Live Archive Statistics + Network Health dashboard.
 * Oracle's primary growth roadmap. Everything comes from the single
 * backend source of truth (/api/stats + /api/admin/network-health);
 * nothing here is hard-coded or manually maintained.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, RefreshCw, Loader2, HeartPulse, TrendingUp, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { adminNetworkHealth } from "../../lib/api";
import { AnimatedStat } from "../AnimatedStat";

const Stat = ({ label, value, testid }) => (
  <div className="border p-3" style={{ borderColor: "#2D2A26", background: "#0f0d0b" }}>
    <div className="font-display text-[22px] text-[#E4DFD3] leading-none">
      <AnimatedStat value={value ?? null} testid={testid} />
    </div>
    <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#7B7168] mt-1.5">{label}</div>
  </div>
);

const SectionLabel = ({ children }) => (
  <div className="font-mono text-[8px] uppercase tracking-[0.3em] text-[#757065] mt-7 mb-2">{children}</div>
);

const EntityRow = ({ r }) => (
  <Link to={`/connections/${r.slug}`}
        className="flex items-center justify-between gap-2 border px-2.5 py-1.5 hover:border-[#D92D20] transition-colors"
        style={{ borderColor: "#2D2A26", background: "#0d0c0a" }}>
    <span className="text-[11.5px] text-[#E4DFD3] truncate">{r.name}</span>
    <span className="font-mono text-[9px] text-[#A68F5E] shrink-0">
      {r.connections} links · {r.dossiers} dossiers{r.has_profile ? "" : " · no profile"}
    </span>
  </Link>
);

export const AdminNetworkHealth = ({ token, userToken }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await adminNetworkHealth(token, userToken)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load network health"); }
    finally { setLoading(false); }
  }, [token, userToken]);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <div className="py-20 text-center" data-testid="network-health-loading">
        <Loader2 size={18} className="animate-spin inline text-[#A68F5E]" />
      </div>
    );
  }
  const { stats, coverage } = data;

  return (
    <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-8" data-testid="network-health-panel">
      <div className="flex flex-wrap items-center gap-3">
        <HeartPulse size={16} className="text-[#D92D20]" />
        <h2 className="font-display text-2xl text-[#E4DFD3]">Network Health</h2>
        <span className="font-mono text-[9px] px-2 py-0.5"
              style={{ background: coverage.health_score >= 70 ? "#3FB950" : coverage.health_score >= 45 ? "#D9A56C" : "#D92D20", color: "#0a0907" }}
              data-testid="health-score">
          SCORE {coverage.health_score}
        </span>
        <button type="button" onClick={load} disabled={loading}
                data-testid="network-health-refresh"
                className="ml-auto inline-flex items-center gap-1 px-2 py-1 border border-[#454038] font-mono text-[8px] uppercase tracking-[0.18em] text-[#A39E93] hover:text-[#E4DFD3] transition-colors">
          {loading ? <Loader2 size={9} className="animate-spin" /> : <RefreshCw size={9} />} Refresh
        </button>
      </div>

      <SectionLabel>Archive Overview — live, single source of truth</SectionLabel>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="archive-overview">
        <Stat label="Published Dossiers" value={stats.published_dossiers} testid="ov-dossiers" />
        <Stat label="Published Profiles" value={stats.published_profiles} testid="ov-profiles" />
        <Stat label="Verified Sources" value={stats.documents} testid="ov-sources" />
        <Stat label="Entities" value={stats.entities} testid="ov-entities" />
        <Stat label="Relationships" value={stats.relationships} testid="ov-relationships" />
        <Stat label="People" value={stats.people} />
        <Stat label="Organizations" value={stats.organizations} />
        <Stat label="Operations" value={stats.operations} />
        <Stat label="Locations" value={stats.locations} />
        <Stat label="Timeline Events" value={stats.timeline_events} />
      </div>

      <SectionLabel>Network Coverage</SectionLabel>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="network-coverage">
        <Stat label="Entities w/ Profiles" value={coverage.entities_with_profiles} />
        <Stat label="Referenced, No Profile" value={coverage.referenced_without_profiles} />
        <Stat label="Avg Links / Entity" value={coverage.avg_connections_per_entity} />
        <Stat label="Profile Coverage %" value={coverage.profile_coverage_pct} />
        <Stat label="Network Coverage %" value={coverage.network_coverage_pct} />
        <Stat label="Relationship Health %" value={coverage.relationship_health_pct} />
      </div>

      {data.context_coverage && (
        <>
          <SectionLabel>Context Coverage — editorial summaries (Connections cards)</SectionLabel>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="context-coverage">
            <Stat label="Public Nodes" value={data.context_coverage.public_nodes} testid="ctx-public-nodes" />
            <Stat label="With Context Summary" value={data.context_coverage.with_summary} testid="ctx-with-summary" />
            <Stat label="Coverage %" value={data.context_coverage.coverage_pct} testid="ctx-coverage-pct" />
            <Link to="/admin/dossiers/epstein?section=entities&context=missing"
                  data-testid="ctx-needs-summary"
                  className="border p-3 hover:border-[#D9A56C] transition-colors"
                  style={{ borderColor: "#2D2A26", background: "#0f0d0b" }}
                  aria-label={`${data.context_coverage.needs_summary} records need a context summary — open editorial list`}>
              <div className="font-display text-[22px] text-[#D9A56C] leading-none">
                {data.context_coverage.needs_summary}
              </div>
              <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#7B7168] mt-1.5">Needs Summary →</div>
            </Link>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid="context-coverage-breakdown">
            {[["people", "People", "person"], ["organizations", "Organizations", "organization"],
              ["locations", "Locations", "property"], ["events", "Events", "event"],
              ["documents", "Documents", "document"], ["aircraft", "Aircraft", "aircraft"],
              ["legal_records", "Legal Records", "legal"], ["other", "Other", ""]].map(([key, label, kind]) => {
              const n = data.context_coverage.missing_by_type?.[key] || 0;
              if (!n) return null;
              return (
                <Link key={key}
                      to={`/admin/dossiers/epstein?section=entities&context=missing${kind ? `&kind=${kind}` : ""}`}
                      data-testid={`ctx-missing-${key}`}
                      aria-label={`${n} ${label} missing a context summary`}
                      className="inline-flex items-center gap-1.5 border px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.12em] text-[#C9C2B4] hover:border-[#D9A56C] hover:text-[#D9A56C] transition-colors"
                      style={{ borderColor: "#2D2A26", background: "#0d0c0a" }}>
                  {label} <span className="text-[#D9A56C]">{n}</span>
                </Link>
              );
            })}
          </div>
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8">
        <div>
          <SectionLabel>Top Connected People</SectionLabel>
          <div className="space-y-1">{data.top_connected.people.map((r) => <EntityRow key={r.slug} r={r} />)}</div>
          <SectionLabel>Top Connected Organizations</SectionLabel>
          <div className="space-y-1">{data.top_connected.organizations.map((r) => <EntityRow key={r.slug} r={r} />)}</div>
          <SectionLabel>Top Connected Dossiers</SectionLabel>
          <div className="space-y-1">
            {data.top_dossiers.map((d) => (
              <Link key={d.slug} to={`/topic/${d.slug}`}
                    className="flex items-center justify-between gap-2 border px-2.5 py-1.5 hover:border-[#D92D20] transition-colors"
                    style={{ borderColor: "#2D2A26", background: "#0d0c0a" }}>
                <span className="text-[11.5px] text-[#E4DFD3] truncate">{d.title}</span>
                <span className="font-mono text-[9px] text-[#A68F5E]">{d.entities} entities</span>
              </Link>
            ))}
          </div>
          <SectionLabel>Most Referenced Sources</SectionLabel>
          <div className="space-y-1">
            {data.most_referenced_sources.map((s) => (
              <div key={s.domain} className="flex justify-between border px-2.5 py-1.5"
                   style={{ borderColor: "#2D2A26", background: "#0d0c0a" }}>
                <span className="font-mono text-[10px] text-[#C9C2B4]">{s.domain}</span>
                <span className="font-mono text-[9px] text-[#A68F5E]">{s.citations} citations</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <SectionLabel><TrendingUp size={9} className="inline mr-1" />Suggested Next Profiles (Oracle roadmap)</SectionLabel>
          <div className="space-y-1" data-testid="suggested-profiles">
            {data.suggested_profiles.map((r) => <EntityRow key={r.slug} r={r} />)}
          </div>
          <SectionLabel>Suggested Next Organizations</SectionLabel>
          <div className="space-y-1">{data.suggested_organizations.map((r) => <EntityRow key={r.slug} r={r} />)}</div>
          <SectionLabel>Weakest Connected Dossiers</SectionLabel>
          <div className="space-y-1">
            {data.weakest_dossiers.map((d) => (
              <Link key={d.slug} to={`/topic/${d.slug}`}
                    className="flex items-center justify-between gap-2 border px-2.5 py-1.5 hover:border-[#D9A56C] transition-colors"
                    style={{ borderColor: "#2D2A26", background: "#0d0c0a" }}>
                <span className="text-[11.5px] text-[#C9C2B4] truncate">{d.title}</span>
                <span className="font-mono text-[9px] text-[#D9A56C]">{d.entities} entities</span>
              </Link>
            ))}
          </div>
          <SectionLabel>Weakest Connected Profiles</SectionLabel>
          <div className="space-y-1">
            {data.weakest_profiles.map((p) => (
              <div key={p.slug} className="flex justify-between border px-2.5 py-1.5"
                   style={{ borderColor: "#2D2A26", background: "#0d0c0a" }}>
                <span className="text-[11.5px] text-[#C9C2B4]">{p.name}</span>
                <span className="font-mono text-[9px] text-[#D9A56C]">{p.cross_links} cross-links</span>
              </div>
            ))}
          </div>

          <SectionLabel>Queues &amp; Integrity</SectionLabel>
          <div className="space-y-1.5 font-mono text-[10px]" data-testid="health-queues">
            <div className="flex items-center gap-2 text-[#C9C2B4]">
              {data.missing_profile_queue.length === 0
                ? <CheckCircle2 size={11} className="text-[#3FB950]" />
                : <AlertTriangle size={11} className="text-[#D9A56C]" />}
              Missing profile queue: {data.missing_profile_queue.length}
            </div>
            <div className="flex items-center gap-2 text-[#C9C2B4]">
              {data.broken_reference_queue.length === 0
                ? <CheckCircle2 size={11} className="text-[#3FB950]" />
                : <AlertTriangle size={11} className="text-[#D92D20]" />}
              Broken references: {data.broken_reference_queue.length}
            </div>
            <div className="flex items-center gap-2 text-[#C9C2B4]">
              {data.duplicate_detection.duplicate_slugs === 0
                ? <CheckCircle2 size={11} className="text-[#3FB950]" />
                : <AlertTriangle size={11} className="text-[#D92D20]" />}
              Duplicate detection: {data.duplicate_detection.status}
              <span className="text-[#7B7168]">({data.duplicate_detection.merge_records} merge records)</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 font-mono text-[8px] text-[#5A554C] flex items-center gap-2">
        <Activity size={9} /> generated {data.generated_at} · all values live from /api/stats — nothing manually maintained
      </div>
    </div>
  );
};
